import { IntakeData } from '../context/IntakeContext'
import { APP_BASE_URL } from './submitIntake'

export function generateIntakeHTML(data: IntakeData): string {
  const styles = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px; }
    .print-btn {
      display: block;
      margin: 0 auto 24px;
      padding: 12px 28px;
      background: #007AFF;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      cursor: pointer;
      text-align: center;
    }
    @media print { .print-btn { display: none !important; } }
    h1 { color: #007AFF; border-bottom: 2px solid #007AFF; padding-bottom: 10px; margin-bottom: 16px; }
    h2 { color: #1a1a1a; margin-top: 30px; background-color: #f8f9fa; padding: 10px; border-radius: 5px; border-left: 4px solid #007AFF; }
    h3 { color: #444; margin-top: 20px; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
    th, td { padding: 10px; border-bottom: 1px solid #eee; text-align: left; vertical-align: top; }
    th { width: 35%; color: #666; font-weight: normal; }
    td { font-weight: bold; }
    .item-box { border: 1px solid #eee; padding: 15px; margin-bottom: 15px; border-radius: 5px; background: #fafafa; }
    .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #999; text-align: center; }
    .file-list { list-style: none; padding: 0; }
    .file-list li { margin-bottom: 10px; padding: 10px; background: #f0f7ff; border-radius: 6px; border: 1px solid #cce4ff; }
    .file-list a { color: #007AFF; text-decoration: none; font-weight: bold; word-break: break-all; }
    .file-list a:hover { text-decoration: underline; }
  `

  const renderRow = (label: string, value: any) => {
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return ''
    }

    let displayValue = value
    if (typeof value === 'boolean') displayValue = value ? 'Ja' : 'Nee'
    else if (value === 'yes') displayValue = 'Ja'
    else if (value === 'no') displayValue = 'Nee'
    else if (Array.isArray(value)) displayValue = value.join(', ')

    return `
      <tr>
        <th>${label}</th>
        <td>${displayValue}</td>
      </tr>
    `
  }

  const renderLinkRow = (label: string, url: string) => {
    if (!url) return ''
    return `
      <tr>
        <th>${label}</th>
        <td><a href="${url}" target="_blank" rel="noopener noreferrer">Bekijk bestand</a></td>
      </tr>
    `
  }

  const renderTable = (title: string, rows: string) => {
    if (!rows.trim()) return ''
    return `<h3>${title}</h3><table>${rows}</table>`
  }

  let html = `
    <!DOCTYPE html>
    <html lang="nl">
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1">
      <title>Noord Digital Intake – ${data.companyName || data.contactName}</title>
      <style>${styles}</style>
    </head>
    <body>
      <button class="print-btn" onclick="window.print()">🖨️ Afdrukken / Opslaan als PDF</button>
      <h1>Nieuwe Website Intake</h1>
      <p>Datum: ${new Date().toLocaleDateString('nl-NL')}</p>

      <h2>1. Contactgegevens</h2>
      <table>
        ${renderRow('Naam', data.contactName)}
        ${renderRow('Functie / Rol', data.contactRole)}
        ${renderRow('E-mailadres', data.contactEmail)}
        ${renderRow('Telefoonnummer', data.contactPhone)}
        ${renderRow('Voorkeur contactmethode', data.contactMethod)}
        ${renderRow('Beste tijd om te bellen', data.bestTime)}
        ${renderRow('Eigenaar', data.isOwner)}
        ${renderRow('Uiteindelijke beslisser', data.isDecisionMaker)}
      </table>
  `

  if (data.isDecisionMaker === 'no' && data.owners.length > 0) {
    html += `<h3>Overige Beslissers / Contactpersonen</h3>`
    data.owners.forEach((owner, i) => {
      html += `
        <div class="item-box">
          <strong>Contactpersoon ${i + 1}</strong><br>
          Naam: ${owner.name}<br>
          Rol: ${owner.role}<br>
          E-mail: ${owner.email}<br>
          Telefoon: ${owner.phone}
        </div>
      `
    })
  }

  html += `
      <h2>2. Team</h2>
      <table>
        ${renderRow('Team tonen op website', data.showTeamOnWebsite)}
        ${renderRow("Foto's beschikbaar", data.teamHasPhotos)}
      </table>
  `

  if (data.teamMembers.length > 0) {
    html += `<h3>Teamleden</h3>`
    data.teamMembers.forEach((member) => {
      html += `
        <div class="item-box">
          <strong>${member.name}</strong> (${member.role})<br>
          Afdeling: ${member.department || '-'}<br>
          Expertise: ${member.expertise || '-'}<br>
          E-mail: ${member.email || '-'}<br>
          Telefoon: ${member.phone || '-'}<br>
          LinkedIn: ${member.linkedin || '-'}<br>
          Bio: ${member.bio || '-'}<br>
          ${member.photo ? `<a href="${member.photo}" target="_blank">Bekijk foto</a>` : ''}
        </div>
      `
    })
  }

  html += `
      <h2>3. Bedrijfsgegevens</h2>
      <table>
        ${renderRow('Bedrijfsnaam', data.companyName)}
        ${renderRow('Handelsnaam', data.tradeName)}
        ${renderRow('Rechtsvorm', data.legalStructure)}
        ${renderRow('Oprichtingsjaar', data.foundingYear)}
        ${renderRow('KVK Nummer', data.cocNumber)}
        ${renderRow('BTW Nummer', data.vatNumber)}
        ${renderRow('Hoofdvestiging Straat', data.mainStreet)}
        ${renderRow('Hoofdvestiging Postcode', data.mainPostcode)}
        ${renderRow('Hoofdvestiging Stad', data.mainCity)}
        ${renderRow('Hoofdvestiging Provincie', data.mainProvince)}
        ${renderRow('Hoofdvestiging Land', data.mainCountry)}
        ${renderRow('Algemeen Telefoonnummer', data.companyPhone)}
        ${renderRow('Algemeen E-mailadres', data.companyEmail)}
        ${renderRow('Huidige Website', data.existingWebsiteUrl)}
        ${renderRow('Social Media Links', data.socialLinks)}
        ${renderRow('Branche / Industrie', data.industry)}
        ${renderRow('Korte bedrijfsomschrijving', data.companyDescription)}
        ${renderRow('Actieve regio', data.activeRegion)}
        ${renderRow('Bereik', data.reach)}
        ${renderRow('Talen website', data.websiteLanguages)}
        ${renderRow('Meerdere locaties', data.multipleLocations)}
        ${renderRow('Locatie strategie', data.locationStrategy)}
      </table>
  `

  if (data.multipleLocations === 'yes' && data.locations.length > 0) {
    html += `<h3>Locaties</h3>`
    data.locations.forEach((loc, i) => {
      html += `
        <div class="item-box">
          <strong>${loc.name || `Locatie ${i + 1}`}</strong><br>
          Adres: ${loc.street}, ${loc.postcode} ${loc.city}, ${loc.province} ${loc.country}<br>
          Telefoon: ${loc.phone || '-'}<br>
          E-mail: ${loc.email || '-'}<br>
          Openingstijden: ${loc.hours || '-'}<br>
          Diensten op deze locatie: ${loc.services || '-'}<br>
          Tonen op website: ${loc.showOnWebsite === 'yes' ? 'Ja' : 'Nee'}
        </div>
      `
    })
  }

  html += `
      <h2>4. Verhaal & Positionering</h2>
  `

  if (data.services.length > 0) {
    html += `<h3>Diensten / Producten</h3>`
    data.services.forEach((service, i) => {
      html += `
        <div class="item-box">
          <strong>${service.name || `Dienst ${i + 1}`}</strong><br>
          ${service.description || '-'}
        </div>
      `
    })
  }

  html += `
      <table>
        ${renderRow('Wie bent u?', data.whoAreYou)}
        ${renderRow('Hoe is het bedrijf ontstaan?', data.howStarted)}
        ${renderRow("Wat maakt u anders (USP's)", data.difference)}
        ${renderRow('Kernwaarden', data.coreValues)}
        ${renderRow('Hoe communiceert u?', data.communicate)}
        ${renderRow('Missie', data.mission)}
        ${renderRow('Visie', data.vision)}
        ${renderRow('Waarom voor u kiezen?', data.whyChooseUs)}
        ${renderRow('Bedrijfsgevoel', data.companyFeel)}
        ${renderRow('Bedrijfsgevoel (Anders)', data.companyFeelOther)}
        ${renderRow('Absoluut vermelden', data.mustInclude)}
        ${renderRow('Absoluut vermijden', data.mustAvoid)}
        ${renderRow('Ideale website omschrijving', data.idealWebsiteDescription)}
      </table>

      <h2>5. Juridisch</h2>
      <table>
        ${renderRow('Heeft juridische documenten', data.hasLegalDocs)}
        ${renderRow('Aanpak documenten', data.legalDocsApproach)}
        ${renderRow('Beschikbare documenten', data.legalDocsList)}
        ${renderRow('Benodigde documenten', data.legalDocsNeeded)}
        ${renderRow('Verwerkt u persoonsgegevens?', data.processData)}
        ${renderRow('Heeft u formulieren op de site?', data.hasForms)}
        ${renderRow('Verstuurt u nieuwsbrieven?', data.hasNewsletter)}
        ${renderRow('Gebruikt u analytics?', data.hasAnalytics)}
        ${renderRow('Cookiebanner nodig?', data.needsCookieBanner)}
        ${renderRow('Specifieke branche-eisen?', data.industryRequirements)}
        ${renderRow('Hulp nodig bij juridisch?', data.needsLegalHelp)}
      </table>
  `

  // Render uploaded legal docs
  const legalDocsRows = Object.entries(data.legalDocs)
    .map(([key, doc]) => renderLinkRow(`Geüpload: ${key}`, doc.file))
    .join('')
  if (legalDocsRows) {
    html += `<table>${legalDocsRows}</table>`
  }

  html += `
      <h2>6. Branding & Huisstijl</h2>
      <table>
        ${renderRow('Heeft visuele identiteit', data.hasIdentity)}
        ${renderLinkRow('Geüpload Logo', data.brandingAssets['logo']?.file || '')}
        ${renderLinkRow('Geüpload Huisstijlhandboek', data.brandingAssets['guidelines']?.file || '')}
        ${renderRow('Wat moet hetzelfde blijven?', data.staySame)}
        ${renderRow('Wat kan beter?', data.improve)}
        ${renderRow('Kleuren die u NIET mooi vindt', data.dislikedColors)}
        ${renderRow('Gewenste visuele stijl', data.visualStyle)}
        ${renderRow('Logo ideeën', data.logoIdeas)}
        ${renderRow('Heeft u een schets?', data.logoSketch)}
        ${renderLinkRow('Geüploade schets', data.logoSketchUpload)}
        ${renderRow('Ideale logo omschrijving', data.idealLogo)}
        ${renderRow('Type logo', data.logoType)}
        ${renderRow('Symbolen/iconen', data.symbols)}
        ${renderRow("Voorbeelden van mooie logo's", data.examples)}
        ${renderRow('Lettertype stijl', data.fontStyle)}
        ${renderRow('Typografie ideeën', data.typographyIdeas)}
        ${renderRow('Typografie keuze', data.typographyChoice)}
        ${renderRow('Iconen stijl', data.iconStyle)}
        ${renderRow('Merkgevoel', data.brandFeel)}
      </table>
  `

  if (data.preferredColors.length > 0) {
    html += `<h3>Merkkleuren</h3><ul>`
    data.preferredColors.forEach((color) => {
      html += `<li>${color.name}: ${color.hex} <span style="display:inline-block;width:15px;height:15px;background-color:${color.hex};border:1px solid #ccc;border-radius:3px;vertical-align:middle;margin-left:5px;"></span></li>`
    })
    html += `</ul>`
  }

  html += `
      <h2>7. Doelen & Doelgroep</h2>
      <table>
        ${renderRow('Primair doel', data.mainGoal)}
        ${renderRow('Doel (Anders)', data.mainGoalOther)}
        ${renderRow('Belangrijkste actie bezoeker (CTA)', data.visitorAction)}
        ${renderRow('Ideale doelgroep', data.targetAudience)}
        ${renderRow('Inspiratie websites', data.inspirationWebsites)}
        ${renderRow('Gewenste indruk', data.desiredImpression)}
        ${renderRow('Welk probleem lost u op?', data.businessProblem)}
        ${renderRow('Waarom bent u beter dan de huidige situatie?', data.betterThanCurrent)}
        ${renderRow('Gewenste Call-to-Actions', data.ctas)}
        ${renderRow("KPI's (Hoe meten we succes?)", data.kpis)}
      </table>

      <h2>8. Type Website & Functionaliteiten</h2>
      <table>
        ${renderRow('Type website', data.websiteType)}
        ${renderRow('Gewenste functionaliteiten', data.features)}
        ${renderRow('Klantenportaal nodig?', data.needsPortal)}
        ${renderRow('Details portaal', data.portalDetails)}
        ${renderRow('Productbeheer', data.productManagement)}
        ${renderRow('Gebruikersrollen', data.userRoles)}
      </table>
  `

  html += renderTable(
    'Webshop Details',
    `
    ${renderRow('Aantal producten', data.numProducts)}
    ${renderRow('Categorieën', data.categories)}
    ${renderRow('Product variaties', data.productVariations)}
    ${renderRow('Type producten', data.productType)}
    ${renderRow('Voorraadbeheer', data.stockManagement)}
    ${renderRow('Verzendmethodes', data.shippingMethods)}
    ${renderRow('Betaalproviders', data.paymentProviders)}
    ${renderRow('Klantaccount omgeving', data.accountArea)}
    ${renderRow('Verlanglijstje', data.wishlist)}
    ${renderRow('Reviews', data.reviews)}
    ${renderRow('Upsells / Cross-sells', data.upsells)}
    ${renderRow('ERP / Boekhouding koppeling', data.erpIntegration)}
  `,
  )

  html += renderTable(
    'Portaal Details',
    `
    ${renderRow('Zichtbaarheid portaal', data.portalVisibility)}
    ${renderRow('Bestanden uploaden', data.portalUploads)}
    ${renderRow('Dashboard functionaliteit', data.portalDashboard)}
    ${renderRow('Statussen inzien', data.portalStatuses)}
    ${renderRow('Notificaties', data.portalNotifications)}
    ${renderRow('Systeem koppeling', data.portalSystemConnection)}
  `,
  )

  html += renderTable(
    'Boekingssysteem Details',
    `
    ${renderRow('Wat wordt er geboekt?', data.bookingItems)}
    ${renderRow('Tijdsloten', data.timeslots)}
    ${renderRow('Meerdere medewerkers', data.multipleStaff)}
    ${renderRow('Bevestigingsmails', data.confirmationEmails)}
    ${renderRow('Vooraf betalen', data.advancePayment)}
    ${renderRow('Agenda koppeling', data.calendarConnection)}
  `,
  )

  html += renderTable(
    'Vacature / Recruitment Details',
    `
    ${renderRow('Vacature archief', data.vacancyArchive)}
    ${renderRow('Filters', data.vacancyFilters)}
    ${renderRow('Sollicitatieformulier', data.applicationForm)}
    ${renderRow('CV Upload', data.cvUpload)}
    ${renderRow('Categorieën', data.vacancyCategories)}
    ${renderRow('Locaties', data.vacancyLocations)}
    ${renderRow('Automatische plaatsing', data.autoPlacement)}
  `,
  )

  html += `
      <h2>9. Pagina's & Content</h2>
      <table>
        ${renderRow("Gewenste pagina's", data.neededPages)}
        ${renderRow("Extra pagina's (Anders)", data.customPageOther)}
        ${renderRow("Pagina's die absoluut moeten", data.mustIncludePages)}
        ${renderRow("Pagina's migreren van oude site", data.migratePages)}
        ${renderRow("Pagina's die mogen verdwijnen", data.disappearPages)}
        ${renderRow('Belangrijkste pagina', data.mostImportantPage)}
        ${renderRow('Content aanlevering', data.existingContent)}
        ${renderRow('Fotografie', data.createContent)}
        ${renderRow('Toon (Tone of Voice)', data.toneOfVoice === 'formal' ? 'Formeel' : data.toneOfVoice === 'informal' ? 'Informeel' : data.toneOfVoice === 'mix' ? 'Mix' : data.toneOfVoice === 'advise' ? 'Adviseer mij' : data.toneOfVoice)}
      </table>

      <h2>10. Techniek & Hosting</h2>
      <table>
        ${renderRow('Heeft u al een website?', data.hasWebsite)}
        ${renderRow('Huidig CMS', data.currentCms)}
        ${renderRow('Heeft domeinnaam', data.domainOwned)}
        ${renderRow('Huidige domeinnaam', data.domainName)}
        ${renderRow('Domein host', data.domainHost)}
        ${renderRow('Domein beheer', data.domainManagement)}
        ${renderRow('3e partij beheerder', data.domainThirdParty)}
        ${renderRow('Gewenste domeinnamen', data.desiredDomains)}
        ${renderRow('Mag website offline tijdens bouw?', data.websiteOfflineAcceptable)}
        ${renderRow('Hosting voorkeur', data.hostingActive)}
        ${renderRow('E-mails gekoppeld aan domein?', data.emailsLinked)}
        ${renderRow('Bestaande analytics?', data.existingAnalytics)}
        ${renderRow('Bestaande pixels?', data.existingPixels)}
        ${renderRow('Koppelingen / Integraties', data.integrations)}
        ${renderRow('Wie beheert content na livegang?', data.manageContent)}
        ${renderRow('Training nodig?', data.needsTraining)}
        ${renderRow('Meerdere beheerders?', data.multipleAdmins)}
      </table>

      <h2>11. SEO & Marketing</h2>
      <table>
        ${renderRow('Belang van SEO', data.seoServices)}
        ${renderRow("Regio's voor SEO", data.seoRegions)}
        ${renderRow('Zoektermen', data.seoSearchTerms)}
        ${renderRow('Concurrenten', data.seoCompetitors)}
        ${renderRow('Zoekwoordenonderzoek', data.seoKeywordResearch)}
        ${renderRow('SEO Silo', data.seoSilo)}
        ${renderRow('SEO Silo pakket', data.seoSiloPackage === 'silver' ? 'Zilver' : data.seoSiloPackage === 'gold' ? 'Goud' : data.seoSiloPackage === 'platinum' ? 'Platinum' : data.seoSiloPackage)}
        ${renderRow("Silo thema's", data.seoSiloThemes)}
        ${renderRow("Silo regio's", data.seoSiloRegions)}
        ${renderRow('Blogposts schrijven', data.seoBlogposts)}
        ${renderRow('Blog frequentie', data.seoBlogFrequency)}
        ${renderRow('Actieve SEO updates', data.seoActiveUpdates)}
        ${renderRow('Google Bedrijfsprofiel', data.seoGoogleBusiness)}
        ${renderRow('Google Bedrijfsprofiel taken', data.seoGoogleBusinessTasks)}
        ${renderRow('Apple Bedrijfsprofiel', data.seoAppleBusiness)}
        ${renderRow('Apple Bedrijfsprofiel taken', data.seoAppleBusinessTasks)}
        ${renderRow('Heatmaps urgentie', data.seoHeatmapsUrgency)}
        ${renderRow('A/B Testing urgentie', data.seoABTestingUrgency)}
        ${renderRow('Review strategie', data.seoReviews)}
        ${renderRow('SEO Training', data.seoTraining)}
        ${renderRow('Google Ads', data.seoGoogleAds)}
        ${renderRow('Social Ads', data.seoSocialAds)}
        ${renderRow('Blogs nodig?', data.needsBlogs)}
        ${renderRow('Leadgeneratie nodig?', data.needsLeadGen)}
        ${renderRow('Marketing tools nodig?', data.needsTools)}
        ${renderRow('Campagnes nodig?', data.needsCampaigns)}
      </table>

      <h2>12. Planning & Workflow</h2>
      <table>
        ${renderRow('Gewenste live datum', data.launchDate)}
        ${renderRow('Harde deadline?', data.hardDeadline)}
        ${renderRow('Reden deadline', data.deadlineReason)}
        ${renderRow('Wie geeft feedback?', data.feedbackGivers)}
        ${renderRow('Aantal stakeholders', data.stakeholdersCount)}
        ${renderRow('Snelheid feedback', data.feedbackSpeed)}
        ${renderRow('Opmerkingen', data.remarks)}
        ${renderRow('Extra notities voor ontwikkelteam', data.extraNotes)}
      </table>
  `

  // Section 13: Uploaded Files
  let uploadsHtml = '<ul class="file-list">'
  let hasUploads = false

  const addFileItem = (name: string, url: string) => {
    if (!url) return
    hasUploads = true
    uploadsHtml += `<li><strong>${name}:</strong> <br><a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a></li>`
  }

  data.teamMembers.forEach((m, i) => {
    addFileItem(`Teamfoto - ${m.name || `Lid ${i + 1}`}`, m.photo)
  })

  Object.entries(data.legalDocs).forEach(([key, doc]) => {
    addFileItem(`Juridisch document (${key})`, doc.file)
  })

  Object.entries(data.brandingAssets).forEach(([key, asset]) => {
    addFileItem(`Branding asset (${key})`, asset.file)
  })

  addFileItem('Logo schets', data.logoSketchUpload)

  uploadsHtml += '</ul>'

  if (hasUploads) {
    html += `
      <h2>13. Aangeleverde Bestanden</h2>
      <p><em>Klik op de links om de bestanden te openen of te downloaden.</em></p>
      ${uploadsHtml}
    `
  }

  html += `
      <div class="footer">
        Dit document is automatisch gegenereerd via de <a href="${APP_BASE_URL}">Noord Digital Intake</a>.
      </div>
    </body>
    </html>
  `

  return html
}
